import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { useValidateXmlLazyQuery } from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";

export type XmlValidationDialogHandle = {
	show: (documentId: string) => void;
	close: () => void;
};

export const XmlValidationDialog = forwardRef<XmlValidationDialogHandle>((_props, ref) => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);

	const [validateXml, { data, loading, called }] = useValidateXmlLazyQuery({
		fetchPolicy: "no-cache",
		onError: (error) => {
			showNotification(error.message, "error");
		},
	});

	useImperativeHandle(
		ref,
		() => ({
			show: (docId: string) => {
				setOpen(true);
				validateXml({
					variables: {
						documentId: docId,
					},
				});
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[validateXml],
	);

	const handleCloseInternal = useCallback(() => {
		setOpen(false);
	}, []);

	const validationResult = data?.validateXml;
	const isValid = validationResult?.isValid ?? false;
	const errors = validationResult?.errors ?? [];

	return (
		<Dialog open={open} maxWidth="md" fullWidth data-testid="xml-validation-dialog">
			<DialogTitle>{t("xml-validation")}</DialogTitle>
			<DialogContent>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
						<CircularProgress />
						<Typography sx={{ ml: 2 }}>{t("loading")}</Typography>
					</Box>
				) : called && validationResult ? (
					isValid ? (
						<Alert
							severity="success"
							icon={<CheckCircleIcon fontSize="inherit" />}
							data-testid="xml-validation-success">
							{t("xml-validation-success")}
						</Alert>
					) : (
						<Box>
							<Alert
								severity="error"
								icon={<ErrorIcon fontSize="inherit" />}
								sx={{ mb: 2 }}
								data-testid="xml-validation-errors-alert">
								{t("xml-validation-errors")}
							</Alert>
							<TableContainer sx={{ maxHeight: 400 }}>
								<Table stickyHeader size="small" data-testid="xml-validation-errors-table">
									<TableHead>
										<TableRow>
											<TableCell sx={{ fontWeight: 600 }}>{t("xml-validation-code")}</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>{t("xml-validation-field")}</TableCell>
											<TableCell sx={{ fontWeight: 600 }}>{t("xml-validation-message")}</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{errors.map((error, index) => (
											<TableRow
												key={index}
												data-testid={`xml-validation-error-row-${index}`}
												sx={{
													"&:nth-of-type(odd)": {
														backgroundColor: "action.hover",
													},
												}}>
												<TableCell sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
													{error.btCode}
												</TableCell>
												<TableCell sx={{ fontFamily: "monospace", fontSize: "0.875rem" }}>
													{error.fieldPath}
												</TableCell>
												<TableCell>{error.message}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)
				) : null}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCloseInternal} data-testid="xml-validation-close-button">
					{t("ok")}
				</Button>
			</DialogActions>
		</Dialog>
	);
});
